import React, { Fragment, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Plus as PlusIcon, UploadCloud as UploadCloudIcon } from 'react-feather'
import { reverse } from 'named-urls'
import { isEmpty } from 'lodash'
import { Skeleton } from '@material-ui/lab'
import { OndemandVideo as OndemandVideoIcon } from '@material-ui/icons'
import {
  Box,
  Button,
  Dialog,
  Grid,
  TablePagination,
  Tooltip,
  useMediaQuery,
} from '@material-ui/core'

import {
  Container,
  ContentHeader,
  LoadingFeedback,
  NoData2,
  Page,
  Permitted,
} from 'components'
import {
  FinalizeDialog,
  ImportFromSegcoopDialog,
  ProjectSettingsModal,
  SummaryCard,
} from './components'

import helpers from 'helpers'

import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import usePagination from 'hooks/usePagination'

import theme from 'theme'
import { routes } from 'Routes'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'
import useAuth from 'hooks/useAuth'

const GovernanceProjectsMain = () => {
  const { company } = useAuth()
  const [openModal, setOpenModal] = useState(false)
  const [openImportModal, setOpenImportModal] = useState(false)
  const [openFinalizedModal, setOpenFinalizedModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [projectData, setProjectData] = useState()
  const [openDialog, setOpenDialog] = useState(false)
  const classes = useStyles()
  const history = useHistory()
  const snackbar = useSnackbar()
  const { perPage, page, handleChangePage, handleChangeRowsPerPage } =
    usePagination(4)

  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'), {
    defaultMatches: true,
  })

  const {
    response: projectResponse,
    isLoading: projectIsLoading,
    refresh: projectRefresh,
  } = useFetch(
    service.dponet.governanceProject.get,
    {
      page,
      perPage,
    },
    [page, perPage],
  )

  const governanceProjects = projectResponse?.data?.governanceProjects

  const hasImportInProgress = governanceProjects?.some((project) =>
    constants.governanceProject.IMPORT_IN_PROGRESS_STATUSES.includes(
      project?.governanceProjectImport?.status,
    ),
  )

  const projectRefreshRef = useRef(null)
  projectRefreshRef.current = projectRefresh

  useEffect(() => {
    if (!hasImportInProgress) return
    const intervalId = setInterval(() => projectRefreshRef.current(), 15000)
    return () => clearInterval(intervalId)
  }, [hasImportInProgress])

  const { response: templateResponse, isLoading: templateIsLoading } = useFetch(
    service.dponet.governanceProjectTemplate.get,
    {},
    [],
  )

  const projectTemplates = templateResponse?.data?.governanceProjectTemplates

  const { response: usersResponse, isLoading: usersIsLoading } = useFetch(
    service.dponet.users.get,
    {
      perPage: 10000,
      minimal: true,
      active: true,
      userCompanyStatus: constants.userCompanies.ACTIVE,
    },
  )

  const users = usersResponse?.data?.users

  const isLoadingRequests =
    projectIsLoading || templateIsLoading || usersIsLoading

  const handleProjectManagerRedirection = (governanceProjectId) => {
    history.push(
      reverse(routes.governanceProjects.manage, { governanceProjectId }),
    )
  }

  const handleOpenDialog = () => setOpenDialog((prevState) => !prevState)

  const handleOpenModal = (governanceProjectData) => {
    setProjectData(governanceProjectData)
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setProjectData()
    setOpenModal(false)
  }

  const handleOpenFinalizedModal = (governanceProjectData) => {
    setProjectData(governanceProjectData)
    setOpenFinalizedModal(true)
  }

  const handleCloseFinalizedModal = () => {
    setProjectData()
    setOpenFinalizedModal(false)
  }

  const handleFinalizedProject = async () => {
    try {
      setLoading(true)

      await service.dponet.governanceProject.changeStatus({
        governanceProjectId: projectData.id,
        status: constants.governanceProject.COMPLETED_STATUS_ID,
      })

      snackbar.open({
        message: 'Projeto finalizado com sucesso!',
        variant: 'success',
      })
      projectRefresh()
      handleCloseFinalizedModal()
    } catch (error) {
      console.error(error)
      snackbar.open({
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Page title="Projetos de Governança">
      <Container maxWidth={false}>
        <LoadingFeedback open={loading} />
        <ContentHeader title="Projetos de Governança">
          <Box width="100%">
            <Tooltip title="Iniciar o vídeo tutorial">
              <Button
                variant="contained"
                color="primary"
                startIcon={<OndemandVideoIcon />}
                onClick={handleOpenDialog}
                fullWidth
              >
                Vídeo Tutorial
              </Button>
            </Tooltip>
          </Box>
          {company?.segcoopIntegration && (
            <Box width="100%">
              <Permitted tag={constants.permissions.GOVERNANCE_PROJECTS.CREATE}>
                <Button
                  color="primary"
                  variant="outlined"
                  startIcon={<UploadCloudIcon />}
                  onClick={() => setOpenImportModal(true)}
                  disabled={isLoadingRequests}
                  fullWidth
                >
                  Importar do Segcoop
                </Button>
              </Permitted>
            </Box>
          )}
          <Box width="100%">
            <Permitted tag={constants.permissions.GOVERNANCE_PROJECTS.CREATE}>
              <Button
                color="primary"
                variant="contained"
                startIcon={<PlusIcon />}
                onClick={() => handleOpenModal()}
                disabled={isLoadingRequests}
                fullWidth
              >
                Criar projeto
              </Button>
            </Permitted>
          </Box>
        </ContentHeader>
        {isLoadingRequests ? (
          <Grid container spacing={2}>
            {[...Array(2)].map((_, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Skeleton
                  key={index}
                  variant="rect"
                  animation="wave"
                  height={759}
                />
              </Grid>
            ))}
          </Grid>
        ) : isEmpty(governanceProjects) ? (
          <NoData2 title="Nenhum projeto de governança foi encontrado." />
        ) : (
          <Fragment>
            <Grid container spacing={2}>
              {governanceProjects?.map((governanceProject) => (
                <Grid key={governanceProject.id} item xs={12} md={6}>
                  <SummaryCard
                    governanceProject={governanceProject}
                    handleProjectManagerRedirection={
                      handleProjectManagerRedirection
                    }
                    handleOpenModal={handleOpenModal}
                    handleOpenFinalizedModal={handleOpenFinalizedModal}
                    refresh={projectRefresh}
                  />
                </Grid>
              ))}
            </Grid>
            <TablePagination
              component="div"
              count={projectResponse?.data?.meta?.totalCount || 0}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              page={page - 1}
              rowsPerPage={perPage}
              rowsPerPageOptions={[4, 8, 16, 20]}
              labelRowsPerPage={isDesktop ? 'Por página' : ''}
              nextIconButtonProps={{ size: 'small' }}
              backIconButtonProps={{ size: 'small' }}
            />
          </Fragment>
        )}
        {openImportModal && (
          <ImportFromSegcoopDialog
            open={openImportModal}
            setOpen={() => setOpenImportModal(false)}
            refresh={projectRefresh}
          />
        )}
        {openModal && (
          <ProjectSettingsModal
            open={openModal}
            setOpen={handleCloseModal}
            setLoading={setLoading}
            isEdit={!!projectData?.id}
            users={users}
            governanceProject={projectData}
            projectTemplates={projectTemplates}
            refresh={projectRefresh}
          />
        )}
        {openFinalizedModal && (
          <FinalizeDialog
            open={openFinalizedModal}
            handleCloseFinalized={handleCloseFinalizedModal}
            handleFinalize={handleFinalizedProject}
            pendingActivities={projectData?.activities?.notFinished || []}
          />
        )}
      </Container>
      <Dialog open={openDialog} onClose={handleOpenDialog} maxWidth="md">
        <Box p={2} width="100%" display="flex" justifyContent="center">
          <iframe
            className={classes.videoPlayer}
            src="https://player.vimeo.com/video/1074767910?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;autoplay=1"
            title="Vídeo tutorial de projetos de governanças"
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
            frameBorder="0"
            allowFullScreen
          />
        </Box>
      </Dialog>
    </Page>
  )
}

export default GovernanceProjectsMain
